import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AnalyzeResult, PortfolioInput, Policy, Position, ScenarioId } from '@prm/shared';
import { DEFAULT_POLICY, knownLeverageFactor } from '@prm/shared';
import { api } from './api';
import { time, usd } from './format';
import { DecisionStrip } from './components/DecisionCard';
import { VulnerabilityPanel } from './components/VulnerabilityPanel';
import { PolicyPanel, PlaybookPanel } from './components/PolicyPanel';
import { MitigationPanel } from './components/MitigationPanel';
import { EventRiskPanel } from './components/EventRiskPanel';
import { BookTable } from './components/BookTable';
import { ChangesView } from './components/ChangesView';
import { HistoryPanel } from './components/Sparkline';
import { PositionsPanel, newDraft, type DraftPosition } from './components/PositionsPanel';
import { PolicyEditor } from './components/PolicyEditor';
import { ExposureCard } from './components/ExposureCard';
import { RegimeCard } from './components/RegimeCard';
import { CorrelationCard } from './components/CorrelationCard';
import { StressCard } from './components/StressCard';
import { RiskTable } from './components/RiskTable';
import { PostureCard } from './components/PostureCard';
import { Collapsible } from './components/Collapsible';

const STORAGE_KEY = 'prm.portfolioId';

const SAMPLE: DraftPosition[] = [
  newDraft({ ticker: 'NVDA', qty: '120', role: 'tactical', conviction: 4 }),
  newDraft({ ticker: 'MSFT', qty: '90', role: 'core', conviction: 5 }),
  newDraft({ ticker: 'AVGO', qty: '60', role: 'tactical', conviction: 3 }),
  newDraft({ ticker: 'UNH', qty: '40', role: 'core', conviction: 4 }),
  newDraft({ ticker: 'XOM', qty: '150', role: 'core', conviction: 3 }),
  newDraft({ ticker: 'SMCI', qty: '200', role: 'probe', conviction: 1 }),
];

const VERDICT_WORD = { reduce: 'REDUCE', hold: 'HOLD', add: 'ADD' } as const;

export default function App() {
  const [portfolioId, setPortfolioId] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));
  const [name, setName] = useState('Main book');
  const [equity, setEquity] = useState('');
  const [positions, setPositions] = useState<DraftPosition[]>(SAMPLE);
  const [policy, setPolicy] = useState<Policy>(DEFAULT_POLICY);
  const [analysis, setAnalysis] = useState<AnalyzeResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [provider, setProvider] = useState<string | null>(null);
  const [dirty, setDirty] = useState(true);
  const [showBook, setShowBook] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [activeScenario, setActiveScenario] = useState<ScenarioId>('current');

  useEffect(() => {
    api.health().then((h) => setProvider(h.provider)).catch(() => setProvider('unreachable'));
  }, []);

  // Restore the last book so a refresh doesn't cost the user their input.
  useEffect(() => {
    if (!portfolioId) return;
    let cancelled = false;

    api
      .getPortfolio(portfolioId)
      .then((p) => {
        if (cancelled) return;
        setName(p.name);
        setEquity(p.settings.equity === null ? '' : String(p.settings.equity));
        setPositions(p.positions.map(toDraft));
        setPolicy(p.settings.policy);
        setDirty(false);
      })
      .catch(() => {
        localStorage.removeItem(STORAGE_KEY);
        if (!cancelled) setPortfolioId(null);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const valid = useMemo(() => positions.filter((p) => p.ticker.trim() !== '' && p.qty.trim() !== ''), [positions]);

  const changePositions = useCallback((next: DraftPosition[]) => {
    setPositions(next);
    setDirty(true);
  }, []);

  const analyze = useCallback(async () => {
    if (valid.length === 0) {
      setError('Add at least one position with a ticker and a quantity.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const input: PortfolioInput = {
        ...(portfolioId ? { id: portfolioId } : {}),
        name: name.trim() === '' ? 'Untitled book' : name,
        settings: { equity: parseNumber(equity), policy },
        positions: valid.map(fromDraft),
      };

      const saved = await api.savePortfolio(input);
      setPortfolioId(saved.id);
      localStorage.setItem(STORAGE_KEY, saved.id);
      setDirty(false);
      setAnalysis(await api.analyze(saved.id));
      setShowBook(false);
      setShowPolicy(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [equity, name, policy, portfolioId, valid]);

  const scenario = analysis?.scenarios.find((s) => s.id === activeScenario) ?? analysis?.scenarios[0] ?? null;

  return (
    <main className="app-shell">
      <header className="topbar">
        <div className="brand">
          <span className="brand__mark">Risk Desk</span>
          <span className="brand__book">{name}</span>
          <span className="brand__meta">
            as of {time(analysis?.asOf)} · {provider ?? '…'}
          </span>
        </div>
        <div className="topbar__actions">
          <button className={`button ${showBook ? 'is-on' : ''}`} onClick={() => setShowBook((v) => !v)}>
            Book
          </button>
          <button className={`button ${showPolicy ? 'is-on' : ''}`} onClick={() => setShowPolicy((v) => !v)}>
            Policy
          </button>
          <button className={`button ${showDetail ? 'is-on' : ''}`} onClick={() => setShowDetail((v) => !v)}>
            Detail
          </button>
          <button className="button button--primary" onClick={analyze} disabled={busy}>
            {busy ? 'Running…' : dirty ? 'Save & analyze' : 'Re-run risk'}
          </button>
        </div>
      </header>

      {error ? <div className="banner">{error}</div> : null}
      {analysis && analysis.warnings.length > 0 ? (
        <div className="banner banner--warn">{analysis.warnings.join(' · ')}</div>
      ) : null}

      {showBook || showPolicy ? (
        <div className="editor-area">
          {showBook ? (
            <PositionsPanel
              positions={positions}
              name={name}
              equity={equity}
              dirty={dirty}
              busy={busy}
              onChange={changePositions}
              onNameChange={(v) => {
                setName(v);
                setDirty(true);
              }}
              onEquityChange={(v) => {
                setEquity(v);
                setDirty(true);
              }}
              onAnalyze={analyze}
            />
          ) : null}
          {showPolicy ? (
            <PolicyEditor
              policy={policy}
              onChange={(next) => {
                setPolicy(next);
                setDirty(true);
              }}
            />
          ) : null}
        </div>
      ) : null}

      {analysis === null || scenario === null ? (
        <div className="panel">
          <div className="empty">
            No analysis yet. Open <strong>Book</strong> to enter positions, then <strong>Save &amp; analyze</strong>.
          </div>
        </div>
      ) : (
        <>
          {/* Each tab is a full engine run against a different book, not a delta. */}
          <section className="scenario-strip" aria-label="Risk scenario">
            <div className="segmented" role="tablist" aria-label="Scenario">
              {analysis.scenarios.map((s) => (
                <button
                  key={s.id}
                  role="tab"
                  aria-selected={s.id === activeScenario}
                  className={`segment ${s.id === activeScenario ? 'is-active' : ''}`}
                  onClick={() => setActiveScenario(s.id)}
                  title={s.description}
                >
                  {s.label}
                  <span className={`segment__verdict segment__verdict--${s.decision.decision}`}>
                    {VERDICT_WORD[s.decision.decision]}
                  </span>
                </button>
              ))}
            </div>
            <div className="source-line">{scenario.description}</div>
          </section>

          {scenario.changes.length > 0 ? (
            <div className="changes">
              <span className="metric-label" style={{ display: 'inline' }}>
                Changes from current
              </span>
              {scenario.changes.slice(0, 10).map((c) => (
                <span className="changes__item" key={c.ticker}>
                  <span className="tkr">{c.ticker}</span>{' '}
                  <span className={c.deltaValue < 0 ? 'text-bad' : 'text-good'}>
                    {c.deltaValue < 0 ? '−' : '+'}
                    {usd(Math.abs(c.deltaValue))}
                  </span>
                </span>
              ))}
            </div>
          ) : null}

          <DecisionStrip scenario={scenario} posture={analysis.posture} changed={analysis.changed} />

          <section className="workspace">
            <div className="primary-column">
              <VulnerabilityPanel scenario={scenario} />
              <PlaybookPanel scenario={scenario} />
            </div>

            <aside className="side-column">
              <MitigationPanel scenario={scenario} />
              <PolicyPanel scenario={scenario} />
              <HistoryPanel
                series={analysis.history}
                bandMax={scenario.regime.band.max}
                volTarget={analysis.settings.policy.volTargetDaily}
                maxDrawdown={analysis.settings.policy.maxStressDrawdown}
              />
              <EventRiskPanel />
            </aside>
          </section>

          <section className="detail-area">
            <BookTable scenario={scenario} />

            {showDetail ? (
              <>
                <Collapsible
                  title="Changes"
                  sub={`${analysis.changeLog.length} recorded transition${analysis.changeLog.length === 1 ? '' : 's'}`}
                >
                  <ChangesView entries={analysis.changeLog} />
                </Collapsible>

                <Collapsible title="Market posture" sub="four components with raw readings">
                  <PostureCard posture={analysis.posture} regime={scenario.regime} />
                </Collapsible>

                <Collapsible title="Regime & Vol" sub="bands, windows, exposure multiplier">
                  <RegimeCard regime={scenario.regime} />
                </Collapsible>

                <Collapsible title="Risk contribution" sub={`${scenario.risk.covarianceWindow}d Euler decomposition`}>
                  <RiskTable risk={scenario.risk} />
                </Collapsible>

                <Collapsible title="Correlation & clusters" sub="30/90d matrix, cluster tiers">
                  <CorrelationCard
                    correlation={scenario.correlation}
                    thresholds={analysis.settings.policy.clusterThresholds}
                  />
                </Collapsible>

                <Collapsible title="Stress scenarios" sub="method and contributors per scenario">
                  <StressCard stress={scenario.stress} />
                </Collapsible>

                <Collapsible title="Exposure, sectors & positions" sub="sector/role/conviction weights, full table">
                  <ExposureCard
                    exposure={scenario.exposure}
                    maxSectorWeight={analysis.settings.policy.maxSectorWeight}
                    maxPositionWeight={analysis.settings.policy.maxPositionWeight}
                  />
                </Collapsible>
              </>
            ) : null}
          </section>
        </>
      )}

      <footer className="footer">
        Informational tool, not financial advice. Figures are derived from delayed third-party market data and may be
        inaccurate or stale; option premiums are modelled estimates, not quotes. Nothing here is a recommendation to
        buy or sell any security — verify every number before acting on it.
      </footer>
    </main>
  );
}

function parseNumber(v: string): number | null {
  const cleaned = v.replace(/[$,_\s]/g, '');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function fromDraft(d: DraftPosition): Omit<Position, 'id'> & { id?: string } {
  const qty = parseNumber(d.qty) ?? 0;
  const ticker = d.ticker.trim().toUpperCase();
  return {
    ticker,
    shares: d.notional ? null : qty,
    value: d.notional ? qty : null,
    costBasis: parseNumber(d.costBasis),
    role: d.role,
    conviction: d.conviction,
    sectorOverride: d.sectorOverride.trim() === '' ? null : d.sectorOverride.trim(),
    leverageFactor: parseNumber(d.leverage) ?? knownLeverageFactor(ticker),
  };
}

function toDraft(p: Position): DraftPosition {
  const notional = p.shares === null;
  return newDraft({
    id: p.id,
    ticker: p.ticker,
    qty: String((notional ? p.value : p.shares) ?? ''),
    notional,
    costBasis: p.costBasis === null ? '' : String(p.costBasis),
    role: p.role,
    conviction: p.conviction,
    sectorOverride: p.sectorOverride ?? '',
    leverage: p.leverageFactor === 1 ? '' : String(p.leverageFactor),
  });
}
